import type { Player } from '@uefadigital/panenka';
import { PkAvatar, PkBadge, PkIcon, PkIdentifier } from '@uefadigital/panenka-react';
import { useTranslation } from 'react-i18next';

import type { MatchEvents } from 'types/ApiResponseInterfaces';
import type { ConfigCompetition } from 'utils/config';
import { getPlayerLink, handleGoToLink } from 'utils/helpers';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

const IN = 'in';
const OUT = 'out';

interface subStoreData {
  [key: string]: Record<string, string>;
}

interface SubstitutionCardData {
  substitution: MatchEvents;
  competition?: ConfigCompetition;
}

export const SubstitutionCard = ({ substitution, competition }: SubstitutionCardData) => {
  const subStore: subStoreData = {
    in: {
      iconName: 'football-substitution-in',
      iconColor: 'support-01',
      textColor: 'pk-text--support-01',
    },
    out: {
      iconName: 'football-substitution-out',
      iconColor: 'support-04',
      textColor: 'pk-text--support-04',
    },
  };

  const { t } = useTranslation();
  const isSmall = window.innerWidth < 600;

  const displayPlayerName = (player: Player) => {
    if (translateEntity(player)?.name) {
      return translateEntity(player).name;
    }

    return t('player');
  };

  const playerPositionToLowerCase = (position: string | undefined) => {
    if (position && position.length > 1) {
      return position.charAt(0) + position.slice(1).toLowerCase();
    }

    return '';
  };

  const showSubstitutePlayer = (player: Player, subAction: string) => {
    const playerLink = competition && getPlayerLink(player.id, competition);

    return (
      <div
        onClick={() => handleGoToLink(playerLink)}
        className={`${style.cardBorderRadius} ${style.pointer} pk-mb--s pk-bg--background pk-d--flex pk-justify-content--between pk-align-items-sm--center`}
      >
        <PkIdentifier className="pk-py--0">
          <span slot="prefix" className="pk-mr--s">
            <PkAvatar
              type="image"
              size={48}
              badge-title="player"
              fallbackImage="generic-head"
              src={player.imageUrl}
              className="pr-avatar-size--40"
            />
          </span>
          <span
            slot="primary"
            className={`${subStore[subAction].textColor} pk-font-size--m pr-font-size--12 pk-line-height--lg`}
          >
            {displayPlayerName(player)}
          </span>
          <span slot="secondary" className="pk-line-height--sm pr-font-size--10">
            {playerPositionToLowerCase(translateEntity(player)?.fieldPosition)}
          </span>
        </PkIdentifier>
        <PkIcon
          style={isSmall ? { transform: 'translate(0 , 40%)' } : {}}
          name={subStore[subAction].iconName}
          color={subStore[subAction].iconColor}
          className="pk-d--inline-block pk-align--middle pk-mr--xs"
        />
      </div>
    );
  };

  return (
    <div className={`pk-shadow--md pk-d--flex pk-flex--column pk-px--m pk-border ${style.cardStyle}`}>
      <div className="pk-d--flex pk-justify-content--between pk-align-items-sm--center pk-my--s">
        <span className="pk-font-size--s pk-line-height--sm pk-text--data-04">{`${substitution.time.minute}'`}</span>
        <PkBadge
          size={32}
          alt="team logo"
          fallbackImage="club-generic-badge"
          src={substitution.primaryActor.team.logoUrl}
        />
      </div>
      {showSubstitutePlayer(substitution.primaryActor.person, OUT)}
      {showSubstitutePlayer(substitution.secondaryActor.person, IN)}
    </div>
  );
};
